/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import { getTeamOfficialTypesBySportId } from '@sportninja/common/selectors/types'
import { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { EditOutlined } from '@ant-design/icons'

import { InviteAction } from '../ActionPanel/ActionPanelActions'
import NewButton from '../Page/NewButton'
import OptionButtonDelete from './OptionButtons/Delete'
import PersonCell from './PersonCell'
import TeamRosterBasedList from './TeamRosterBasedList'
import List from '.'
import MoreOptions from './OptionButtons/More'
import { formatPhoneNumber } from 'react-phone-number-input'
import TeamOfficialModal from '../TeamOfficialModal'

export const AddTeamOfficial = ({
  permission,
  teamId,
  rosterId,
  onComplete,
  sportId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleSuccess = useCallback(() => {
    onComplete()
  }, [onComplete])

  if (!permission.create) {
    return null
  }

  return (
    <>
      <NewButton onClick={handleOpenModal} />
      <TeamOfficialModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        teamId={teamId}
        rosterId={rosterId}
        onSuccess={handleSuccess}
        sportId={sportId}
      />
    </>
  )
}

const TeamOfficialList = ({
  teamId,
  teamName,
  permission,
  teamOfficialTypes,
  refreshKey,
  onComplete,
  sportId,
}) => {
  const [sort, setSort] = useState('name_last')
  const [direction, setDirection] = useState('asc')

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOfficial, setSelectedOfficial] = useState(null)

  const handleEditClick = useCallback((item) => {
    setSelectedOfficial(item)
    setIsModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    setSelectedOfficial(null)
  }, [])

  const headers = [
    {
      text: t('common:name'),
      sizePercent: 34,
      // sort: 'name_full',
      Cell: PersonCell,
    },
    {
      text: t('common:role'),
      sizePercent: 19,
      // sort: 'official_type',
      minWidth: 90,
      accessor: (item) => {
        return teamOfficialTypes.find((t) => t.id === item.team_official_type)
          ?.name_full
      },
    },
    {
      text: t('common:email'),
      sizePercent: 34,
      minWidth: 240,
      // sort: 'email',
      accessor: 'email',
    },
    {
      text: t('common:phone'),
      sizePercent: 34,
      minWidth: 240,
      // sort: 'email',
      accessor: 'phone_number',
      Cell: ({ item }) => {
        return item?.phone_number ? formatPhoneNumber(item?.phone_number) : ''
      },
    },
    {
      text: 'Invite Status',
      sizePercent: 13,
      // sort: 'is_accepted',
      minWidth: 80,
      accessor: (item) => {
        let status = ''
        if (item?.is_invited) {
          status = 'Invited'
        }
        if (item?.is_accepted) {
          status = 'Accepted'
        }

        return status
      },
    },
  ]

  return (
    <TeamRosterBasedList
      teamId={teamId}
      teamName={teamName}
      permission={permission}
      slug='team-officials'
      refreshKey={refreshKey}
      onComplete={onComplete}
    >
      {(roster, refresh) => {
        const handleModalSuccess = () => {
          refresh()
        }

        const options = (item) => {
          const slug = `/teams/${teamId}/rosters/${roster.id}/team-officials/${item.id}`

          const opts = [
            <button
              key='edit'
              type='button'
              onClick={() => handleEditClick(item)}
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                color: ${colors.PRIMARY};
                padding: 4px 8px;
                font-size: 18px;
                display: flex;
                align-items: center;
                justify-content: center;
                &:hover {
                  opacity: 0.8;
                }
              `}
              title={t('Web:editTeamOfficial')}
            >
              <EditOutlined />
            </button>,
            <InviteAction
              key='invite'
              invite={{
                is_invited: item.is_invited,
                is_accepted: item.is_accepted,
                invitation_id: item.invitation_id,
              }}
              user={{
                id: item.id,
                name_first: item.name_first,
                name_last: item.name_last,
                team_official_type_id: item.team_official_type,
                email: item?.email,
              }}
              slug={slug}
              onComplete={refresh}
            />,
            <MoreOptions
              key='more'
              options={[
                {
                  key: 'remove',
                  Component: OptionButtonDelete,
                  componentProps: {
                    buttonText: 'remove',
                    slug,
                    iconName: 'user-minus',
                    title: `Remove ${item.name_full}`,
                    type: t('common:teamOfficial'),
                    itemId: item.id,

                    onComplete: refresh,
                  },
                  title: 'remove',
                  iconName: 'user-minus',
                },
              ]}
            />,
          ]

          return opts.filter((o) => o !== undefined)
        }

        return (
          <>
            <List
              headers={headers.filter((o) => o !== undefined)}
              options={options}
              listProps={{
                items: roster?.team_officials || [],
                sort,
                direction,
                setSort: (s) => {
                  setSort(s)
                },
                setDirection: (d) => {
                  setDirection(d)
                },
              }}
              noItemsText={t('common:teamOfficial_plural', { count: 2 })}
            />
            <TeamOfficialModal
              isOpen={isModalOpen}
              onClose={handleModalClose}
              initialData={selectedOfficial}
              teamId={teamId}
              rosterId={roster.id}
              onSuccess={handleModalSuccess}
              sportId={sportId}
            />
          </>
        )
      }}
    </TeamRosterBasedList>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    teamOfficialTypes: getTeamOfficialTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(TeamOfficialList)
